/**
 * Convert a LinkedIn URN into an ID. This gracefully handle URNs which are already IDs.
 *
 * @param urn - Either ID or URN (Uniform Resource Name) of resource
 * @param urnPrefix - Prefix of the URN
 * @returns - ID of resource
 * @see https://developer.linkedin.com/docs/guide/v2/concepts/urns
 */
export function convertLinkedInUrnToId<TId extends string = string>(urn: string, urnPrefix: string): TId {
    if (String(urn).startsWith(urnPrefix)) {
        return urn.split(':').slice(-1)[0] as TId;
    } else {
        return urn as TId;
    }
}

/**
 * Convert a LinkedIn ID into a URN. This gracefully handle IDs which are already in URN format.
 *
 * @param id - The value to convert
 * @param urnPrefix - Prefix of the URN
 * @returns A LinkedIn URN
 * @see https://developer.linkedin.com/docs/guide/v2/concepts/urns
 */
export function convertLinkedInIdToUrn<TUrn extends string = string>(id: string, urnPrefix: string): TUrn {
    if (String(id).startsWith(urnPrefix)) {
        return id as TUrn;
    } else {
        const optionalColon = urnPrefix.endsWith(':') ? '' : ':';
        return `${urnPrefix}${optionalColon}${id}` as TUrn;
    }
}

export interface LinkedInPaginationResult<T> {
    paging: {
        count: number;
        start: number;
    };
    elements: T[];
}
